import { Immutable } from "immer";
import React from "react";

import { constants } from "@reactivated/constants";

import { BundleGroupTypes, ConfiguratorTypes } from "../../../constants";
import { IAddon } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { usePageSetting } from "../../../utils/settings";
import { ConfiguratorUpsell } from "../ConfiguratorUpsell";
import { ConfiguratorUpsellContainer } from "../ConfiguratorUpsellContainer";
import { UpsellInfoModal } from "../ConfiguratorUpsellInfoModal";
import { defaults } from "../defaults";
import { setSelectedUpgrade, updateSelectedAddons } from "../reducers";
import { baseVariantSelector } from "../selectors";

const AppSlug = constants["tsicommon.types.AppSlug"];

export enum OptionPriceType {
    RELATIVE,
    ABSOLUTE,
    FREE_GIFT,
}

const handleUpgradeSelectionChange = (
    updates: IAddon[],
    setSelectedUpgrade: (productID: IProductID | null) => void,
): void => {
    if (updates.length > 0) {
        const update = updates[0];
        setSelectedUpgrade(update.quantity > 0 ? update.productID : null);
    }
};

export const ConfiguratorUpsells = (
    props: Immutable<{
        configuratorType: ConfiguratorTypes;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const baseVariant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return baseVariantSelector(state);
    });

    const selectedUpgrade = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.selectedUpgrade;
    });

    const selectedAddons = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.selectedAddons;
    });

    const promoCards = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.promoCards;
    });

    const appSlug = usePageSetting("app_slug");

    if (!baseVariant) {
        return null;
    }

    const selectedUpgradeFormatted = selectedUpgrade
        ? [
              {
                  productID: selectedUpgrade,
                  quantity: 1,
              },
          ]
        : [];

    const promoCardsBlock = promoCards
        ? promoCards.map((promo) => (
              <ConfiguratorUpsell
                  key={promo.id}
                  bundleID={null}
                  bundleType={BundleGroupTypes.PROMO_CARD}
                  sectionTitle={promo.section_title}
                  lineOne={promo.card_title}
                  lineTwo={promo.product_name}
                  lineThree={promo.description}
                  isSelected={false}
                  cardImage={promo.card_image_url}
                  flair={
                      promo.flair && promo.flair.length > 0
                          ? promo.flair[0].value
                          : null
                  }
                  infoModal={promo.info_modal}
                  configuratorType={props.configuratorType}
              />
          ))
        : null;

    const freeGiftsBlock = (
        <ConfiguratorUpsellContainer
            optionPriceType={OptionPriceType.FREE_GIFT}
            bundleType={BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT}
            selectedUpgrades={selectedAddons}
            onSelectionChange={(updates) =>
                dispatch(updateSelectedAddons(updates))
            }
        />
    );

    // 1-800-Pillows renders product upgrades as a Standard-vs-Upgrade radio
    // pair; other sites use the default card grid. Site-detection stays here
    // (the container is presentation-agnostic).
    const productUpgradesBlock = (
        <ConfiguratorUpsellContainer
            optionPriceType={OptionPriceType.RELATIVE}
            bundleType={BundleGroupTypes.UPGRADE_PRODUCT}
            selectedUpgrades={selectedUpgradeFormatted}
            onSelectionChange={(updates) =>
                handleUpgradeSelectionChange(updates, (productID) =>
                    dispatch(setSelectedUpgrade(productID)),
                )
            }
            useRadioStyle={appSlug === AppSlug.ONE800PILLOWS}
        />
    );

    const changeModelUpgradesBlock = (
        <ConfiguratorUpsellContainer
            optionPriceType={OptionPriceType.RELATIVE}
            bundleType={BundleGroupTypes.UPGRADE_MODEL}
            selectedUpgrades={selectedUpgradeFormatted}
            onSelectionChange={(updates) =>
                handleUpgradeSelectionChange(updates, (productID) =>
                    dispatch(setSelectedUpgrade(productID)),
                )
            }
        />
    );

    const addOnsBlock = (
        <ConfiguratorUpsellContainer
            optionPriceType={OptionPriceType.ABSOLUTE}
            bundleType={BundleGroupTypes.IN_CONFIGURATOR_ADD_ON}
            selectedUpgrades={selectedAddons}
            onSelectionChange={(updates) =>
                dispatch(updateSelectedAddons(updates))
            }
        />
    );

    const upsellInfoModal = (
        <UpsellInfoModal
            configuratorType={props.configuratorType}
            selectedUpgrade={selectedUpgrade}
            selectedAddons={selectedAddons}
        />
    );

    // 1-800-Pillows surfaces free gifts + product upgrades before promo cards;
    // other sites lead with promo cards. Order is the only difference between
    // the two layouts — render once with a per-site order array.
    const blockMap = {
        promoCards: promoCardsBlock,
        freeGifts: freeGiftsBlock,
        productUpgrades: productUpgradesBlock,
        changeModelUpgrades: changeModelUpgradesBlock,
        addOns: addOnsBlock,
        upsellInfoModal: upsellInfoModal,
    } satisfies Record<string, React.ReactNode>;
    const order: ReadonlyArray<keyof typeof blockMap> =
        appSlug === AppSlug.ONE800PILLOWS
            ? [
                  "freeGifts",
                  "productUpgrades",
                  "promoCards",
                  "changeModelUpgrades",
                  "addOns",
                  "upsellInfoModal",
              ]
            : [
                  "promoCards",
                  "freeGifts",
                  "productUpgrades",
                  "changeModelUpgrades",
                  "addOns",
                  "upsellInfoModal",
              ];

    return (
        <>
            {order.map((key) => (
                <React.Fragment key={key}>{blockMap[key]}</React.Fragment>
            ))}
        </>
    );
};
